export const REASON_PRESETS = [
  { code: 'LOW_STOCK', label: 'Low stock' },
  { code: 'SEASONAL_DEMAND', label: 'Seasonal demand' },
  { code: 'CUSTOMER_ORDERS', label: 'Customer orders' },
  { code: 'PROMOTION', label: 'Promotion' },
  { code: 'MANUAL_RESTOCK', label: 'Manual restock' },
  { code: 'OTHER', label: 'Other' },
];

export function parseSafeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function getVariantLabel(variant) {
  if (!variant) return null;
  if (variant.optionValues && Object.keys(variant.optionValues).length) {
    return Object.values(variant.optionValues).join(' / ');
  }
  return variant.title || variant.sku || null;
}

export function getVariantThreshold(variant, productThreshold = 10) {
  return parseSafeNumber(variant?.low_stock_threshold, parseSafeNumber(productThreshold, 10));
}

export function getVariantStockState(variant, productThreshold = 10) {
  const qty = parseSafeNumber(variant?.quantity, 0);
  const threshold = getVariantThreshold(variant, productThreshold);

  if (qty < 0) return 'NEGATIVE';
  if (qty === 0) return 'OUT';
  if (qty <= threshold) return 'LOW';
  return 'SUFFICIENT';
}

export function getVariantDefaultRequestQty(variant, productThreshold = 10) {
  const qty = parseSafeNumber(variant?.quantity, 0);
  const threshold = getVariantThreshold(variant, productThreshold);
  return Math.max(threshold * 2 - qty, threshold, 1);
}

export function normalizeRequestedQty(value) {
  if (value === '' || value == null) return '';
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return '';
  if (n < 1) return 1;
  return n;
}

export function variantSearchText(variant) {
  const label = getVariantLabel(variant) || '';
  const sku = variant?.sku || '';
  const optionText = variant?.optionValues ? Object.entries(variant.optionValues).map(([k, v]) => `${k}:${v}`).join(' ') : '';
  return `${label} ${sku} ${optionText}`.toLowerCase();
}

export function inferOptionKeys(variants) {
  const keySets = new Map();
  for (const v of variants || []) {
    for (const [k, val] of Object.entries(v?.optionValues || {})) {
      if (!keySets.has(k)) keySets.set(k, new Set());
      keySets.get(k).add(val);
    }
  }
  return [...keySets.entries()]
    .sort((a, b) => a[1].size - b[1].size || a[0].localeCompare(b[0]))
    .map(([k]) => k);
}

export function distinctValuesForKey(variants, key) {
  if (!key) return [];
  return [...new Set((variants || []).map((v) => v?.optionValues?.[key] || '(Unspecified)'))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
}

export function buildVariantGroups(variants, groupByKey = null) {
  const allKeys = inferOptionKeys(variants);
  if (!allKeys.length) {
    return {
      meta: { allKeys: [], groupByKey: null, primary: null, secondary: null },
      groups: [{ key: 'all', label: 'All Variants', rows: variants || [] }],
    };
  }
  const effectiveKey = groupByKey && allKeys.includes(groupByKey) ? groupByKey : allKeys[0];

  const map = new Map();
  for (const v of variants || []) {
    const val = v?.optionValues?.[effectiveKey] || '(Unspecified)';
    const gKey = `${effectiveKey}:${val}`;
    const label = `${effectiveKey}: ${val}`;
    if (!map.has(gKey)) map.set(gKey, { key: gKey, label, groupValue: val, rows: [] });
    map.get(gKey).rows.push(v);
  }

  const groups = [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  return {
    meta: { allKeys, groupByKey: effectiveKey, primary: allKeys[0] || null, secondary: allKeys[1] || null },
    groups,
  };
}

export function filterVariants(variants, {
  query = '',
  stockFilter = 'all',
  attrFilters = {},
  primaryKey = null,
  primaryValue = 'all',
  secondaryKey = null,
  secondaryValue = 'all',
  includeInactive = false,
} = {}, productThreshold = 10) {
  const q = query.trim().toLowerCase();
  const merged = { ...attrFilters };
  if (primaryKey && primaryValue !== 'all') merged[primaryKey] = primaryValue;
  if (secondaryKey && secondaryValue !== 'all') merged[secondaryKey] = secondaryValue;

  return (variants || []).filter((v) => {
    if (!includeInactive && v?.active === 0) return false;

    const stockState = getVariantStockState(v, productThreshold);
    if (stockFilter === 'out' && stockState !== 'OUT') return false;
    if (stockFilter === 'low' && stockState !== 'LOW' && stockState !== 'NEGATIVE') return false;
    if (stockFilter === 'sufficient' && stockState !== 'SUFFICIENT') return false;

    for (const [key, val] of Object.entries(merged)) {
      if (val && val !== 'all' && (v?.optionValues?.[key] || '(Unspecified)') !== val) return false;
    }

    if (q && !variantSearchText(v).includes(q)) return false;
    return true;
  });
}

export function sortVariants(variants, sortBy = 'stockAsc', productThreshold = 10) {
  const arr = [...(variants || [])];
  arr.sort((a, b) => {
    if (sortBy === 'stockAsc') return parseSafeNumber(a.quantity, 0) - parseSafeNumber(b.quantity, 0);
    if (sortBy === 'stockDesc') return parseSafeNumber(b.quantity, 0) - parseSafeNumber(a.quantity, 0);
    if (sortBy === 'sku') return String(a.sku || '').localeCompare(String(b.sku || ''));
    if (sortBy === 'status') return getVariantStockState(a, productThreshold).localeCompare(getVariantStockState(b, productThreshold));
    return String(getVariantLabel(a) || '').localeCompare(String(getVariantLabel(b) || ''));
  });
  return arr;
}

export function selectedMap(variantItems) {
  const map = new Map();
  for (const i of variantItems || []) map.set(String(i.variantId), i);
  return map;
}

export function calcSummary(variants, variantItems, productThreshold = 10) {
  const selected = selectedMap(variantItems);
  let totalUnits = 0;
  let invalidQtyCount = 0;
  let outSelected = 0;
  let lowSelected = 0;
  let sufficientSelected = 0;
  let negativeStockSelected = 0;

  for (const v of variants || []) {
    const item = selected.get(String(v.id));
    if (!item) continue;

    const qty = parseSafeNumber(item.requestedQuantity, 0);
    if (qty < 1) invalidQtyCount += 1;
    totalUnits += Math.max(qty, 0);

    const state = getVariantStockState(v, productThreshold);
    if (state === 'OUT') outSelected += 1;
    if (state === 'LOW') lowSelected += 1;
    if (state === 'SUFFICIENT') sufficientSelected += 1;
    if (state === 'NEGATIVE') negativeStockSelected += 1;
  }

  return {
    selectedCount: selected.size,
    totalUnits,
    invalidQtyCount,
    outSelected,
    lowSelected,
    sufficientSelected,
    negativeStockSelected,
  };
}

export function reasonToText(reasonPreset, reasonNote) {
  const note = String(reasonNote || '').trim();
  if (!reasonPreset) return note;
  if (reasonPreset === 'OTHER') return note;
  const label = REASON_PRESETS.find((r) => r.code === reasonPreset)?.label || reasonPreset;
  return note ? `${label}: ${note}` : label;
}
